import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Badge,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Button,
  Chip,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  ShoppingCart as OrderIcon,
  Description as QuoteIcon,
  Construction as ProjectIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  PersonAdd as UserIcon,
  Info as InfoIcon,
  LocalOffer as PromoIcon,
  Build as SystemIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { useSocket } from '../../context/SocketContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    isConnected,
  } = useSocket();

  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open && isConnected) {
      setLoading(true);
      fetchNotifications(1, 10);
      setTimeout(() => setLoading(false), 500);
    }
  }, [open, isConnected, fetchNotifications]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
    handleClose();
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const handleViewAll = () => {
    navigate('/notifications');
    handleClose();
  };

  const getNotificationIcon = (type) => {
    const iconProps = { fontSize: 'small' };
    switch (type) {
      case 'order_created':
      case 'order_status':
        return <OrderIcon {...iconProps} />;
      case 'order_payment':
      case 'payment_received':
      case 'payment_submitted':
        return <PaymentIcon {...iconProps} />;
      case 'quote_created':
      case 'quote_response':
        return <QuoteIcon {...iconProps} />;
      case 'project_update':
      case 'project_milestone':
        return <ProjectIcon {...iconProps} />;
      case 'consultation_created':
        return <ProjectIcon {...iconProps} />;
      case 'low_stock':
        return <WarningIcon {...iconProps} />;
      case 'new_user':
        return <UserIcon {...iconProps} />;
      case 'promotion':
        return <PromoIcon {...iconProps} />;
      case 'system':
        return <SystemIcon {...iconProps} />;
      default:
        return <InfoIcon {...iconProps} />;
    }
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'urgent') return '#ef4444';
    if (priority === 'high') return '#f59e0b';
    
    switch (type) {
      case 'order_created':
      case 'order_status':
        return '#0ea5e9';
      case 'order_payment':
      case 'payment_received':
      case 'payment_submitted':
        return '#22c55e';
      case 'quote_created':
      case 'quote_response':
        return '#8b5cf6';
      case 'project_update':
      case 'project_milestone':
        return '#06b6d4';
      case 'consultation_created':
        return '#0ea5e9';
      case 'low_stock':
        return '#ef4444';
      case 'new_user':
        return '#3b82f6';
      case 'promotion':
        return '#ec4899';
      default:
        return '#64748b';
    }
  };

  const formatTime = (date) => {
    try {
      return dayjs(date).fromNow();
    } catch {
      return '';
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: open ? '#0ea5e9' : 'inherit',
          '&:hover': { bgcolor: alpha('#0ea5e9', 0.1) },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              height: 18,
              minWidth: 18,
            },
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 480,
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#f8fafc',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#0c4a6e' }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                color="error"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllRead}
              sx={{ fontSize: '0.75rem', color: '#0ea5e9' }}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {/* Notification List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#0ea5e9' }} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No notifications yet</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 340, overflow: 'auto' }}>
            {notifications.slice(0, 10).map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  alignItems="flex-start"
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: notification.isRead ? 'transparent' : alpha('#0ea5e9', 0.05),
                    '&:hover': { bgcolor: alpha('#0ea5e9', 0.1) },
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: alpha(getNotificationColor(notification.type, notification.priority), 0.15),
                        color: getNotificationColor(notification.type, notification.priority),
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={notification.isRead ? 500 : 700}
                          sx={{
                            color: notification.isRead ? 'text.secondary' : '#0c4a6e',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.isRead && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#0ea5e9',
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.5,
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#94a3b8', fontSize: '0.7rem' }}
                        >
                          {formatTime(notification.createdAt)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Footer */}
        {notifications.length > 0 && (
          <Box
            sx={{
              p: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Button
              fullWidth
              size="small"
              onClick={handleViewAll}
              sx={{ color: '#0ea5e9', fontWeight: 600 }}
            >
              View All Notifications
            </Button>
          </Box>
        )}

        {/* Connection Status - only show if there are supposed to be notifications */}
        {!isConnected && notifications.length > 0 && (
          <Box
            sx={{
              p: 1,
              bgcolor: alpha('#f59e0b', 0.1),
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ color: '#b45309' }}>
              Reconnecting to server...
            </Typography>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationDropdown;
